import { DATA_PATHS } from './app_core/src/dataPaths.js';
import { checkDatabaseVersion } from '/app_core/src/utils/cache/data-cache.js';

export const DOMAIN_NAME = 'canaryspots.com';
export const APP_TITLE = 'Canary Spots';

// SEO Settings
export const SEO_SETTINGS = {
    SITE_NAME: 'Canary Spots',
    SITE_DESCRIPTION: 'Explore amazing destinations with our interactive travel map.',
    PUBLISHER_NAME: 'Canary Spots',
    PUBLISHER_URL: 'https://canaryspots.com'
};

// App Version - Change this to force a cache update for versioned assets. Change the number to force a cache update from users that have the old version.
export const APP_VERSION = '1.2.1';

// Image Paths - Domain-specific image assets
export const IMAGE_PATHS = {
    LOGO: '/images/logo/logo.webp',
    ICONS: '/images/icons/',
    FLAGS: '/images/flags/'
};

// CSS Paths - Domain-specific stylesheets
export const CSS_PATHS = {
    ROOT: '/root.css',
    APP: '/app_core/src/app.css'
};

// Language Settings
export const LANGUAGE_SETTINGS = {
    DEFAULT_LANGUAGE: 'en', // Change to 'en' for English or 'es' for Spanish by default
    SUPPORTED_LANGUAGES: ['en', 'es'], // Languages supported by this domain
    LANGUAGE_NAMES: {
        'en': 'English',
        'es': 'Español'
    },
    // Control which language flags are displayed in the header menu
    SHOW_LANGUAGE_FLAGS: {
        'en': true,  // Show English flag
        'es': true
    }
};

// Ads Settings (for future Google Ads, etc.)
export const ADS_SETTINGS = {
    ENABLED: false,           // Master switch for all ads
    GOOGLE_ADS: false         // Enable Google Ads
};

// Analytics Settings (Google Tag Manager / Google Analytics tracking)
export const ANALYTICS_SETTINGS = {
    GOOGLE_ANALYTICS_ENABLED: false // Enable Google Tag Manager analytics tracking (impressions, clicks, conversions, user interactions)
};

// Blog Settings
export const BLOG_ENABLED = false; // Enable/disable blog functionality


// Map Data Strategy
// 'UNIFIED': Single set of map files in /data/ (e.g. Canary Spots)
// 'PER_AREA': Separate map files in /data/{area_slug}/ (e.g. Mapa Sudaca)
export const MAP_DATA_STRATEGY = {
    TYPE: 'UNIFIED',

    // If true, map loads immediately on app start (good for UNIFIED), if area was chosen already in localStorage before!
    // If false, map waits for Area Selector to pick an area (mandatory for PER_AREA)
    LOAD_ON_START: true,

    // Visual options for UNIFIED mode (only applies when TYPE === 'UNIFIED')
    UNIFIED_VISUAL_OPTIONS: {
        // If true, unselected areas are greyed out. If false, they use normal colors.
        GREY_OUT_UNSELECTED: true,

        // If true, roads are visible on unselected areas. If false, roads are hidden.
        SHOW_UNSELECTED_ROADS: false
    },

    // If true, show clickable area pins on the map for unselected areas (UNIFIED mode only)
    // Allows users to change areas by clicking the pin in the center of an unselected area
    SELECT_AREA_FROM_MAP: true
};

// Map Settings
export const MAP_SETTINGS = {
    DEFAULT_CENTER: [28.2916, -16.6291], // Canary Islands
    DEFAULT_ZOOM: 8,
    MIN_ZOOM: 8,  // Prevent zooming out too far from the islands
    MAX_ZOOM: 16,
    DETAIL_ZOOM: 14,
    BOUNDS_PADDING: [100, 100],
    // Enable map bounds restriction to keep focus on Canary Islands
    MAX_BOUNDS_ENABLED: true,
    MAX_BOUNDS: [
        [25.0, -20.5], // More south and west - extra space
        [32.0, -11.0]  // More north and east - extra space
    ],
    // Viscosity is a factor that controls how much the map can be zoomed in or out. From 0.1 to 1.0
    MAX_BOUNDS_VISCOSITY: 1  // Higher viscosity to keep users within bounds
};

// Map Layer Settings - Control which layers are visible
export const MAP_LAYERS = {
    BACKGROUND: true,  // Base map background (includes borders merged into it)
    BORDERS: false,    // Disabled - borders are now merged into background layer
    ROADS: true        // Road network
};

// Map Layer Paths

// Show default area spots (from spots_x_area table)
export const SHOW_AREA_SPOTS = true;

export const MAP_LAYER_PATHS = DATA_PATHS.MAP_LAYERS;

// Directions Settings
// USE_GOOGLE_CID: If true, use Google Maps CID for direct place links when available. If false, always fallback to lat/lng.
export const DIRECTIONS_SETTINGS = {
    USE_GOOGLE_CID: true // Set to false to always use lat/lng for directions links
};

// GeoJSON Layer Settings
export const GEOJSON_SETTINGS = {
    STYLE: {
        weight: 1,
        fillOpacity: 1,
        opacity: 0.5
    },
    RENDERER: {
        PADDING: 0.5,
        TOLERANCE: 3
    },
    INTERACTIVE: false,
    PANE: 'overlayPane',
    SMOOTH_FACTOR: 1
};

// Header Variant Settings
export const HEADER_VARIANT = 'buttons';
// 'buttons' for the two buttons header (Explore and Discover/Maps)
// 'search' for the search bar (with the areas and tags dropdowns)

// Header Button Configuration
export const HEADER_BUTTONS = {
    SHOW_EXPLORE: true,
    SHOW_DISCOVER: false //now MAPS
};

// Header Button Startup Options
export const HEADER_EXPLORE_DEFAULT = false;
export const HEADER_DISCOVER_DEFAULT = false;
// Only one should be true for startup, but both can be false (neither auto-opens)

// Marker Settings
export const MARKER_SIZE = {
    SCALE: 0.8 // 1 = 100%, 0.75 = 75%, etc. This scale multiplies all dimensions from MARKER_STYLE
};

// Spot Display Mode: 'POPUPS_INFO' (full info popup), 'POPUPS_IMAGE' (compact image-only popup), 'CARDS' (bottom banner)
export const SPOT_DISPLAY_MODE = 'POPUPS_IMAGE';

// Cards Settings For ANY MODE (POPUPS or CARDS, for CARDS should be true always!)
export const CARDS_SETTINGS = {
    ENABLED: true,           // Master switch for all cards
    BANNER_DELAY: 0,         // Delay in milliseconds before showing cards banner (0 = appear immediately with popup)
    BANNER_DISPLAY: {
        SHOW_TITLE: false,        // Show title in cards banner
        SHOW_CLOSE_BUTTON: false,  // Show close (X) button in cards banner
        SHOW_BADGE: true          // Show badge (affiliate link tag, info, etc.) in cards banner
    },
    // Animation Settings for Cards Banner
    ANIMATION: {
        TYPE: 'fade',   // 'slide' (slide up/down + fade) or 'fade' (fade in/out only)
        DURATION: 400    // Duration in milliseconds
    },
    SHOW_TAG_CARDS: true,    // Show cards linked to tags (explore flow)
    PARTNERS: {
        CIVITATIS: false,     // Civitatis tours and activities
        BOOKING_COM: false,   // Booking.com hotels and flights
        GETYOURGUIDE: false,  // GetYourGuide tours
        VIATOR: false,        // Viator tours and activities
        AIRBNB: false,        // Airbnb accommodations
        HOTELS_COM: false,    // Hotels.com accommodations
        EXPEDIA: false,       // Expedia travel
        TRIPADVISOR: false,   // TripAdvisor reviews and bookings
        OTHER: true          // Other cards (not really partners)
    }
};

// Cards Header Separator - use tag icon between area and tag (fallback to dot when false)
export const CARDS_HEADER_USE_TAG_ICON = true;

// Spot Card image strip limits (controls how many thumbnails show above cards)
export const SPOT_CARD_IMAGE_LIMIT = {
    DESKTOP: 3,
    MOBILE: 4
};

// Marker Style Configuration (switch between 'pin' and 'circle')
// - Do NOT change the design of 'pin' (default). 'circle' reuses the same inner circle and icon.
export const MARKER_STYLE = {
    TYPE: 'circle', // 'pin' | 'circle'
    PIN: {
        width: 46,
        height: 50,
        circle: 32,
        icon: 16,
        // Bottom-center of pin tip
        anchor: [46 / 2, 50],
        // Popup above the marker
        popupAnchor: [0, -50]
    },
    CIRCLE: {
        // For circle style the container equals the circle size
        width: 32,
        height: 32,
        circle: 32,
        icon: 20,
        // Center of the circle
        anchor: [32 / 2, 32 / 2],
        // Popup above the circle center
        popupAnchor: [0, -20]
    }
};

// Popup Settings
export const POPUP_SETTINGS = {
    MIN_WIDTH: 200,
    PADDING: 10
};

// Search Settings
export const SEARCH_SETTINGS = {
    MAX_SPOTS_TO_SHOW: 20,
    RESULT_MAX_ZOOM: 12 // Prevents zooming in too close. Lower number = more zoomed out.
};

// Dynamic zoom thresholds for search results
export const SEARCH_ZOOM_THRESHOLDS = [
    { maxDistance: 1000, zoom: 12 },
    { maxDistance: 3000, zoom: 11 },
    { maxDistance: 8000, zoom: 11 },
    { maxDistance: 20000, zoom: 10 },
    { maxDistance: 50000, zoom: 10 },
    { maxDistance: 200000, zoom: 10 },
    { maxDistance: Infinity, zoom: 9 }
];

// Simplified zoom behavior settings for clarity and maintainability
export const ZOOM_BEHAVIOR_SETTINGS = {
    // zoom for one spot
    SINGLE_MARKER_ZOOM: 10,

    // zoom out
    MIN_ZOOM: 8,

    // zoom in
    MAX_ZOOM: 16,

    // Padding (in pixels) around markers when fitting the map to them
    // Increase for more space around spots, decrease for a tighter fit
    BOUNDS_PADDING: [50, 50]
};

// Tag Search Settings for multi-tag search
export const TAG_SEARCH_SETTINGS = {
    MAX_TAGS_PER_SEARCH: 2,
    COLOR_EXACT_MATCH: '#7B8FA1', // muted blue-grey for exact match
    COLOR_ONE_MATCH: '#B0B6BE'    // soft grey for partial match
};

// Explore Settings for tag selection in Explore flow
export const EXPLORE_TAG_SETTINGS = {
    MAX_TAGS_PER_EXPLORE: 1  // Maximum number of tags that can be selected in Explore mode
};

// Favorites/Spots-by-ID Settings (for the favorites and the url with direct spots)
//(se pueden poner mas que max_spots_per_id_request porque la app los muestra en "chunks" de 20)

export const FAVORITES_SETTINGS = {
    MAX_SPOTS_TO_SAVE: 30  // Maximum favorites PER AREA (not total)
};

// EXPLORE_PACK_SLUGS is deprecated. Pack detection is now dynamic and performed
// at runtime using the area's packs (via `getPacksForArea`). Keeping a hardcoded
// list here is error-prone and has been removed to avoid stale configuration.
// If you need a local override for testing, consider using a small startup shim.

// NOTE: Previously, EXPLORE_PACK_SLUGS listed canonical pack slugs such as 'nature' or 'places'.
// That list has been removed.

export const URL_SETTINGS = {
    IDs: true,
    search: false,
    explore: true,
    discover: true, // now MAPS
    favorites: true
};

// Explore Settings
export const EXPLORE_SETTINGS = {
    ALLOW_SHOW_ALL_SPOTS: false  // If false, "all" or "default" tags will show empty map instead of all spots
};

// Favorites Storage Settings
export const FAVORITES_STORAGE = {
    LOCAL: 'local_storage',
    MYSQL: 'mysql'
};
export const FAVORITES_MODE = FAVORITES_STORAGE.LOCAL;


// Area Selector Settings
export const AREA_SELECTOR = {
    ENABLED: true,
    STYLE: 'carousel', // 'carousel' | 'list' | 'search'
    BACKGROUND: 'solid', // 'solid' | 'blurred_map'
    SHOW_FOOTER_TEXT: false // Show informative text at bottom
};

// Map Modes Configuration
export const MAP_MODES = {
    AVAILABLE: ['explore', 'discover'], // Which modes are active
    // No default means we show the selector buttons after area selection
};


// Map Animation Settings
export const MAP_ANIMATION_SETTINGS = {
    TYPE: 'fade', // 'pan' or 'fade'
    PAN_DURATION_SECONDS: 1, // The duration of the smooth pan animation
    FADE_DURATION_MS: 300, // Duration for fade in/out for map/spots/cards
    EXTRA_FADE_FOR_CARDS: 50 // Extra duration only for cards animation
};

// Logging Settings
// Add new categories as needed for diagnostics (e.g., IMAGES for image/asset loading)
export const LOGGING = {
    ENABLED: false,       // Master switch for all logs
    DEBUG: false,         // Verbose debug logs for diagnostics
    CACHE: true,         // Logs for cache hits and misses
    FILTERING: false,     // Logs for search filtering
    SORTING: false,      // Logs for sorting operations
    VISIBILITY: false,    // Logs for showing/hiding spots
    ADS: false,          // Logs for ads and affiliate system
    BLOG: false,         // Logs for blog system and posts
    GENERAL: false,      // General application-level logs
    MENU: false,         // Logs for left menu interactions
    IMAGES: false        // Logs for image/asset loading and errors
};

// --- Always-on version logs (not controlled by LOGGING) ---
// Log App Version (from settings.js or meta tag)
try {
    const appVersion = (typeof APP_VERSION !== 'undefined') ? APP_VERSION : (document.querySelector('meta[name="app-version"]')?.content || 'unknown');
    console.log(`[APP VERSION] ${appVersion}`);
} catch (e) {
    console.log('[APP VERSION] unknown (error reading version)');
}

// Log Database Version (async, from API)
try {
    checkDatabaseVersion()
        .then(({ dbVersion, imagesVersion } = {}) => {
            const v = dbVersion || 'unknown';
            console.log(`[DB VERSION] ${v}`);
        })
        .catch(() => console.log('[DB VERSION] unknown (fetch error)'));
} catch (e) {
    console.log('[DB VERSION] unknown (error)');
}

// Storage Keys
export const STORAGE_KEYS = {
    FAVORITES: 'favorites',
    LANGUAGE: 'language'
};

// Max spot IDs per request for get-spots-by-ids endpoint
export const MAX_SPOT_IDS_PER_REQUEST = 50;

// Marker Popup Display Settings
export const MARKER_POPUP_DISPLAY = {
    title: true,
    city: false,
    description: true,
    tags: true,
    distance: true,
    lightbox: true // Enable lightbox for carousel images
};

// Marker Card Display Settings (applies to the cards header, mainly for POPUPS_IMAGE mode)
export const MARKER_CARD_DISPLAY = {
    distance: true, // Show distance in the card header (e.g., "2.1 km from you")
    tags: false     // Show tags in the card header (kept false for now)
};

// Marker Hover Display Settings
export const MARKER_HOVER_DISPLAY = {
    title_hover: false,  // Show title on hover
    id_hover: false   // Show ID on hover (useful for testing)
};

// Marker Popup Credits Display Settings (for POPUPS_INFO and POPUPS_IMAGE modes)
export const MARKER_POPUP_CREDITS = {
    general_credits: false,   // Show photographer/author credits (other photographers)
    our_credits: false       // Show domain credits (e.g., "Canary Spots")
};

// Marker Popup Action Buttons Settings (for POPUPS_INFO and POPUPS_IMAGE modes)
export const MARKER_POPUP_ACTION_BUTTONS = {
    heart: false,      // Favorite/like button
    directions: false, // Directions button
    share: false       // Share button
};

// Card Action Buttons Settings (for CARDS on ANY mode that shows cards)
export const CARD_ACTION_BUTTONS = {
    heart: false,      // Favorite/like button
    directions: false, // Directions button
    share: false      // Share button
};

// Dynamic Map Action Buttons - swap map share/location with spot actions when a spot is selected
export const MAP_DYNAMIC_ACTION_BUTTONS = {
    ENABLED: true, // Set true to enable spot-based buttons (share, favorite, directions)
    BUTTONS: {
        share: true,
        favorite: true,
        directions: true
    }
};

// Cards Header Layout - Center tabs when no action buttons present
export const CARDS_HEADER_CENTER_TABS = true; // If true, center tabs in header when no action buttons are shown

// If true, automatically open the popup when only one spot is shown
export const AUTO_OPEN_SINGLE_POPUP = true;

// Marker Tag Display Settings
export const MARKER_TAG_SETTINGS = {
    MAX_TAGS: 2,
    SHOW_SEARCHED_TAG_FIRST: true,
    SHOW_OTHER_TAGS: 'random' // or 'alphabetical'
};

// UI Visibility Settings - Single source of truth for controlling visibility of map controls FOR 
export const UI_VISIBILITY = {
    // Individual button visibility - desktop and mobile control
    BUTTONS: {
        ZOOM: {
            DESKTOP: true,  // Show zoom buttons on desktop
            MOBILE: false    // Show zoom buttons on mobile
        },
        LOCATION: {
            DESKTOP: true,  // Show location button on desktop
            MOBILE: true    // Show location button on mobile
        },
        SHARE: {
            DESKTOP: true,  // Show share button on desktop
            MOBILE: true   // Show share button on mobile
        },
        // Spot URL / web icon buttons (controlled separately from other map buttons)
        SPOT_URL_1: {
            DESKTOP: true,  // Show spot URL 1 button on desktop
            MOBILE: true    // Show spot URL 1 button on mobile
        },
        SPOT_URL_2: {
            DESKTOP: false,  // Show spot URL 2 button on desktop
            MOBILE: false   // Show spot URL 2 button on mobile
        }
        ,
        // Spot-level share button visibility (separate from the global share control)
        SPOT_SHARE: {
            DESKTOP: false,  // Show spot-level share button on desktop
            MOBILE: false    // Show spot-level share button on mobile
        }
    },

    // Hide map controls when popup is open (for POPUPS mode only)
    HIDE_CONTROLS_ON_POPUPS: {
        DESKTOP: false,     // Hide controls on desktop when popup is open
        MOBILE: false        // Hide controls on mobile when popup is open
    },

    // Hide map controls when card banner is open (for CARDS mode only)
    HIDE_CONTROLS_ON_CARD: {
        DESKTOP: false,     // Hide controls on desktop when card banner is open
        MOBILE: true        // Hide controls on mobile when card banner is open
    }
};


export const MAP_LABELS_ENABLED = false; // Show labels/names of the areas on the map

// Security Settings (for testing - disable in production)
export const SECURITY_SETTINGS = {
    ENABLED: false,           // Master switch for all security features (rate limiting, anti-scraping)
    RATE_LIMITING: false,     // Enable/disable rate limiting
    ANTI_SCRAPING: false      // Enable/disable anti-scraping protection
};

// Attribution Settings
export const ATTRIBUTION_SETTINGS = {
    SHOW_IN_MAP: true,    // Show attribution in bottom-right corner of map
    SHOW_IN_MENU: true,   // Show detailed attribution in left menu
    AUTO_HIDE_SECONDS: 8  // Auto-hide map attribution after X seconds (0 = never hide)
};

// Left Menu Settings - Control which items appear in the left menu
export const MENU_SETTINGS = {
    LANGUAGE_SELECTOR: true,  // Language switcher flags
    AREA_SELECTOR: true,      // Change area button (uses AREA_SELECTOR.ENABLED as master)
    FAVORITES: true,          // Show favorites button
    ABOUT: true,              // "What is [App]?" button
    SUPPORT: false,            // "Buy me a Coffee" button
    CONTACT: true,            // "Contact" button
    LOGO: true,               // Logo in footer
    MAP_DATA: true,           // Map data attribution text
    LEGAL_LINKS: true,        // Master switch for legal links footer
    // Individual legal links (only shown if LEGAL_LINKS is true)
    PRIVACY: true,
    TERMS: true,
    COOKIES: true,
    TRANSPARENCY: false,
    LICENSES: true,
    QUICK_GUIDE: true,        // "Show Quick Guide" button
    SHOW_GUIDE_ON_STARTUP: false // Show the guide naturally on first visit (startup)
};